import { Check, CheckCheck, ImagePlus, Minus, Send, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatData, ChatMessageData, ChatTranslations } from '@/types/chat';

interface ChatConversationProps {
    chat: ChatData;
    messages: ChatMessageData[];
    currentUserId: number;
    translations: ChatTranslations;
    isTyping: boolean;
    isSending: boolean;
    isResolved: boolean;
    supportOnline: boolean;
    onSend: (body: string | null, images?: File[]) => Promise<void>;
    onResolve?: () => void;
    onMarkAsRead: () => void;
    onTyping: (typing: boolean) => void;
    onClose: () => void;
    onDismissResolved?: () => void;
    hideClose?: boolean;
    isMinimizeAction?: boolean;
    /** Customer name shown in header subtitle (admin view) */
    customerName?: string;
    /** URL to edit the customer (admin view, opens in new tab) */
    customerEditUrl?: string;
    /** URL to view the order (admin view, opens in new tab) */
    orderUrl?: string;
    /** Use a single-line input instead of textarea */
    singleLineInput?: boolean;
}

export function ChatConversation({
    chat,
    messages,
    currentUserId,
    translations: t,
    isTyping,
    isSending,
    isResolved,
    supportOnline,
    onSend,
    onResolve,
    onMarkAsRead,
    onTyping,
    onClose,
    onDismissResolved,
    hideClose,
    isMinimizeAction,
    customerName,
    customerEditUrl,
    orderUrl,
    singleLineInput,
}: ChatConversationProps) {
    const [imagePreviews, setImagePreviews] = useState<
        { file: File; url: string }[]
    >([]);
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const singleInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        onMarkAsRead();
    }, [messages.length, onMarkAsRead]);

    const hasScrolledRef = useRef(false);
    useEffect(() => {
        const scroll = () => {
            messagesEndRef.current?.scrollIntoView(
                hasScrolledRef.current ? { behavior: 'smooth' } : undefined,
            );
            hasScrolledRef.current = true;
        };
        // Wait for images/DOM to settle before scrolling
        requestAnimationFrame(() => requestAnimationFrame(scroll));
    }, [messages.length, isTyping]);

    const handleSend = useCallback(async () => {
        const body = inputValue.trim() || null;
        const files =
            imagePreviews.length > 0
                ? imagePreviews.map((p) => p.file)
                : undefined;

        if (!body && !files) return;

        setInputValue('');
        await onSend(body, files);
        imagePreviews.forEach((p) => URL.revokeObjectURL(p.url));
        setImagePreviews([]);
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            singleInputRef.current?.focus();
        });
    }, [onSend, imagePreviews, inputValue]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );

    const handleImageSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;

            const newPreviews = Array.from(files)
                .slice(0, 4)
                .map((file) => ({
                    file,
                    url: URL.createObjectURL(file),
                }));

            setImagePreviews((prev) => [...prev, ...newPreviews].slice(0, 4));

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [],
    );

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setInputValue(e.target.value);
            onTyping(true);
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            typingTimeoutRef.current = setTimeout(() => {
                onTyping(false);
            }, 1500);
        },
        [onTyping],
    );

    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="flex h-full flex-col bg-white dark:bg-background">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-border dark:bg-card">
                <div className="flex flex-col gap-0.5">
                    {orderUrl ? (
                        <a
                            href={orderUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-semibold text-gray-900 hover:text-gray-600 dark:text-foreground dark:hover:text-primary"
                        >
                            {t.order ?? 'Order'} {chat.order_number} &rarr;
                        </a>
                    ) : (
                        <span className="text-sm font-semibold text-gray-900 dark:text-foreground">
                            {t.order ?? 'Order'} {chat.order_number}
                        </span>
                    )}
                    {customerName ? (
                        <div>
                            {customerEditUrl ? (
                                <a
                                    href={customerEditUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-[11px] font-medium text-amber-600 hover:text-amber-500 dark:text-amber-300 dark:hover:text-amber-200"
                                >
                                    {customerName} &rarr;
                                </a>
                            ) : (
                                <span className="text-[11px] text-gray-500 dark:text-muted-foreground">
                                    {customerName}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5">
                            <span
                                className={`inline-block size-1.5 rounded-full ${supportOnline ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-muted-foreground/50'}`}
                            />
                            <span className="text-[11px] text-gray-500 dark:text-muted-foreground">
                                {supportOnline
                                    ? (t.support_online ?? 'Support online')
                                    : (t.support_offline ?? 'Support offline')}
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {!isResolved && onResolve && (
                        <button
                            onClick={onResolve}
                            className="cursor-pointer rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200/60 hover:text-gray-800 dark:text-muted-foreground dark:hover:bg-accent dark:hover:text-accent-foreground"
                        >
                            {t.resolve ?? 'Resolve'}
                        </button>
                    )}
                    {!hideClose && (
                        <button
                            onClick={onClose}
                            className="cursor-pointer rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-gray-200/60 hover:text-gray-600 dark:hover:bg-accent dark:hover:text-accent-foreground"
                            aria-label={
                                isMinimizeAction
                                    ? (t.minimize ?? 'Minimize chat')
                                    : (t.close ?? 'Close')
                            }
                            title={
                                isMinimizeAction
                                    ? (t.minimize ?? 'Minimize chat')
                                    : (t.close ?? 'Close')
                            }
                        >
                            {isMinimizeAction ? (
                                <Minus className="size-4" />
                            ) : (
                                <X className="size-4" />
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto bg-white px-4 py-3 dark:bg-background">
                {/* Category label */}
                <div className="mb-4 flex justify-center">
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] text-gray-500 dark:bg-muted dark:text-muted-foreground">
                        {chat.category_label}
                    </span>
                </div>

                {/* Messages */}
                <div>
                    {messages.map((msg, index) => {
                        const isOutgoing = msg.user.id === currentUserId;
                        const prevMsg = messages[index - 1];
                        const nextMsg = messages[index + 1];
                        const isFirstInGroup =
                            !prevMsg || prevMsg.user.id !== msg.user.id;
                        const isLastInGroup =
                            !nextMsg || nextMsg.user.id !== msg.user.id;

                        // Messenger-style rounded corners:
                        // flatten the inner edge where consecutive bubbles meet
                        let bubbleRadius = 'rounded-2xl';
                        if (!isFirstInGroup || !isLastInGroup) {
                            if (isOutgoing) {
                                if (isFirstInGroup)
                                    bubbleRadius = 'rounded-2xl rounded-br-md';
                                else if (isLastInGroup)
                                    bubbleRadius = 'rounded-2xl rounded-tr-md';
                                else
                                    bubbleRadius = 'rounded-l-2xl rounded-r-md';
                            } else {
                                if (isFirstInGroup)
                                    bubbleRadius = 'rounded-2xl rounded-bl-md';
                                else if (isLastInGroup)
                                    bubbleRadius = 'rounded-2xl rounded-tl-md';
                                else
                                    bubbleRadius = 'rounded-r-2xl rounded-l-md';
                            }
                        }

                        return (
                            <div
                                key={msg.id}
                                className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'} ${isFirstInGroup ? 'mt-2' : 'mt-0.5'}`}
                            >
                                {/* Avatar column for incoming messages */}
                                {!isOutgoing && (
                                    <div className="mr-1.5 flex w-6 shrink-0 items-end">
                                        {isLastInGroup && msg.user.avatar ? (
                                            <img
                                                src={msg.user.avatar}
                                                alt={msg.user.name}
                                                className="size-6 rounded-full object-cover"
                                            />
                                        ) : isLastInGroup ? (
                                            <span className="flex size-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-medium text-gray-500 dark:bg-muted dark:text-muted-foreground">
                                                {msg.user.name
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </span>
                                        ) : null}
                                    </div>
                                )}
                                <div
                                    className={`flex max-w-[75%] flex-col ${isOutgoing ? 'items-end' : 'items-start'}`}
                                >
                                    {!isOutgoing && isFirstInGroup && (
                                        <span className="mb-0.5 px-1 text-[11px] font-medium text-gray-500 dark:text-muted-foreground">
                                            {msg.user.name}
                                        </span>
                                    )}

                                    {msg.images.length > 0 && (
                                        <div
                                            className={`mb-0.5 grid gap-0.5 ${msg.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} overflow-hidden ${bubbleRadius}`}
                                        >
                                            {msg.images.map((img, imgIdx) => (
                                                <button
                                                    key={imgIdx}
                                                    type="button"
                                                    onClick={() =>
                                                        setLightboxSrc(img.url)
                                                    }
                                                    className={`cursor-pointer overflow-hidden ${msg.images.length === 3 && imgIdx === 2 ? 'col-span-2' : ''}`}
                                                >
                                                    <img
                                                        src={img.thumb}
                                                        alt=""
                                                        className={`w-full object-cover ${msg.images.length === 3 && imgIdx === 2 ? 'max-h-22' : 'max-h-48'}`}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {msg.body && (
                                        <div
                                            className={`${bubbleRadius} px-3 py-1.5 text-[13px] leading-relaxed ${
                                                isOutgoing
                                                    ? 'text-white'
                                                    : 'bg-gray-100 text-gray-900 dark:bg-secondary dark:text-secondary-foreground'
                                            }`}
                                            style={
                                                isOutgoing
                                                    ? {
                                                          backgroundColor:
                                                              '#0084FF',
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {msg.body}
                                        </div>
                                    )}

                                    {isLastInGroup && (
                                        <span className="mt-px flex items-center gap-0.5 px-1 text-[10px] text-gray-400 dark:text-muted-foreground/80">
                                            {formatTime(msg.created_at)}
                                            {isOutgoing &&
                                                (msg.is_read ? (
                                                    <CheckCheck className="size-2.5 text-blue-500" />
                                                ) : (
                                                    <Check className="size-2.5" />
                                                ))}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/* Typing indicator */}
                    {isTyping && (
                        <div className="mt-2 flex items-end justify-start">
                            <div className="mr-1.5 w-6 shrink-0" />
                            <div className="rounded-2xl bg-gray-100 px-4 py-2.5 dark:bg-muted">
                                <div className="flex items-center gap-1">
                                    <span className="size-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s] dark:bg-muted-foreground" />
                                    <span className="size-1.5 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s] dark:bg-muted-foreground" />
                                    <span className="size-1.5 animate-bounce rounded-full bg-gray-400 dark:bg-muted-foreground" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Resolved banner */}
            {isResolved && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 text-center dark:border-border dark:bg-card">
                    <p className="text-sm text-gray-500 dark:text-muted-foreground">
                        {t.chat_resolved ?? 'This chat has been resolved.'}
                    </p>
                    {onDismissResolved && (
                        <button
                            onClick={onDismissResolved}
                            className="mt-2.5 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-800 dark:bg-amber-600 dark:hover:bg-amber-500"
                        >
                            <X className="size-3" />
                            {t.close ?? 'Close'}
                        </button>
                    )}
                </div>
            )}

            {/* Image previews */}
            {imagePreviews.length > 0 && !isResolved && (
                <div className="flex gap-2 overflow-x-auto border-t border-gray-200 bg-gray-50 px-4 py-2 dark:border-border dark:bg-card">
                    {imagePreviews.map((preview, idx) => (
                        <div key={idx} className="group relative shrink-0">
                            <img
                                src={preview.url}
                                alt=""
                                className="size-12 rounded-lg border border-gray-200 object-cover dark:border-border"
                            />
                            <button
                                onClick={() => {
                                    URL.revokeObjectURL(preview.url);
                                    setImagePreviews((prev) =>
                                        prev.filter((_, i) => i !== idx),
                                    );
                                }}
                                className="absolute -top-1 -right-1 flex size-4 cursor-pointer items-center justify-center rounded-full bg-gray-700 text-white opacity-0 transition-opacity group-hover:opacity-100"
                            >
                                <X className="size-2.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Input area */}
            {!isResolved && (
                <div className="flex items-center gap-2 border-t border-gray-200 bg-white px-4 py-3 dark:border-border dark:bg-card">
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="shrink-0 cursor-pointer rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-muted-foreground dark:hover:bg-accent dark:hover:text-accent-foreground"
                        title={t.attach_image ?? 'Attach image'}
                    >
                        <ImagePlus className="size-5" />
                    </button>
                    {singleLineInput ? (
                        <input
                            ref={singleInputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                onTyping(true);
                                if (typingTimeoutRef.current) {
                                    clearTimeout(typingTimeoutRef.current);
                                }
                                typingTimeoutRef.current = setTimeout(() => {
                                    onTyping(false);
                                }, 1500);
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={t.type_message ?? 'Type a message...'}
                            className="h-10 flex-1 rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-900 transition-colors outline-none placeholder:text-gray-400 focus:border-gray-400 dark:border-input dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground dark:focus:border-primary/60"
                        />
                    ) : (
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder={t.type_message ?? 'Type a message...'}
                            rows={1}
                            className="max-h-20 min-h-[36px] flex-1 resize-none rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-900 transition-colors outline-none placeholder:text-gray-400 focus:border-gray-300 focus:bg-white dark:border-input dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground dark:focus:border-primary/60"
                        />
                    )}
                    <button
                        onClick={handleSend}
                        disabled={
                            isSending &&
                            !inputValue.trim() &&
                            imagePreviews.length === 0
                        }
                        className="shrink-0 cursor-pointer rounded-lg p-1.5 transition-opacity hover:opacity-70 disabled:opacity-30"
                        style={{ color: '#0084FF' }}
                    >
                        <Send className="size-5" />
                    </button>
                </div>
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageSelect}
            />

            {/* Image lightbox */}
            {lightboxSrc && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
                    onClick={() => setLightboxSrc(null)}
                >
                    <button
                        type="button"
                        onClick={() => setLightboxSrc(null)}
                        className="absolute top-4 right-4 cursor-pointer rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
                    >
                        <X className="size-5" />
                    </button>
                    <img
                        src={lightboxSrc}
                        alt=""
                        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}
